import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";
import { toast } from "react-toastify";
import styles from "../Management.module.css";

const API = `${process.env.REACT_APP_API_BASE_URL}/api/phlebotomists`;
const CITIES_API = `${process.env.REACT_APP_API_BASE_URL}/api/cities`;

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const emptyForm = { name: "", mobile_number: "", address: "", city: "", pincode: "" };

const Phlebotomists = () => {
  const [phlebs, setPhlebs] = useState([]);
  const [cities, setCities] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Add modal
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);

  // Edit modal
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const fetchPhlebs = useCallback(async () => {
    try {
      const res = await axios.get(API);
      setPhlebs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to fetch phlebotomists:", e);
      toast.error("Failed to load phlebotomists.");
    }
  }, []);

  useEffect(() => {
    fetchPhlebs();
    axios
      .get(CITIES_API)
      .then((res) => setCities(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [fetchPhlebs]);

  const q = query.trim().toLowerCase();
  const filtered = phlebs.filter((p) => {
    if (!q) return true;
    return (
      (p.name || "").toLowerCase().includes(q) ||
      (p.city || "").toLowerCase().includes(q) ||
      String(p.pincode || "").toLowerCase().includes(q) ||
      (p.mobile_number || "").toLowerCase().includes(q)
    );
  });

  // ─── Add ───
  const openAdd = () => {
    setAddForm(emptyForm);
    setShowAdd(true);
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) return toast.warn("Name is required.");
    setLoading(true);
    try {
      const payload = {
        name: addForm.name.trim(),
        mobile_number: addForm.mobile_number.trim() || undefined,
        address: addForm.address.trim() || undefined,
        city: addForm.city.trim() || undefined,
        pincode: addForm.pincode.trim() || undefined,
      };
      const res = await axios.post(API, payload, authHeader());
      setPhlebs((prev) => [...prev, res.data]);
      setShowAdd(false);
      setAddForm(emptyForm);
      toast.success("Phlebotomist added.");
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to create phlebotomist.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Edit ───
  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      name: p.name || "",
      mobile_number: p.mobile_number || "",
      address: p.address || "",
      city: p.city || "",
      pincode: p.pincode || "",
    });
    setShowEdit(true);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setEditing(null);
    setEditForm(emptyForm);
  };

  const handleEdit = async () => {
    if (!editForm.name.trim()) return toast.warn("Name is required.");
    setLoading(true);
    try {
      // PUT is partial — send the editable fields (null clears a value)
      const payload = {
        name: editForm.name.trim(),
        mobile_number: editForm.mobile_number.trim() || null,
        address: editForm.address.trim() || null,
        city: editForm.city.trim() || null,
        pincode: editForm.pincode.trim() || null,
      };
      await axios.put(`${API}/${editing.id}`, payload, authHeader());
      setPhlebs((prev) =>
        prev.map((x) => (x.id === editing.id ? { ...x, ...payload } : x))
      );
      closeEdit();
      toast.success("Phlebotomist updated.");
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to update phlebotomist.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Delete (soft) ───
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete phlebotomist "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/${p.id}`, authHeader());
      setPhlebs((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Phlebotomist deleted.");
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to delete phlebotomist.");
    }
  };

  // ─── Export ───
  const handleExport = () => {
    if (!filtered.length) return toast.warn("No phlebotomists to export.");
    const data = filtered.map((p) => ({
      ID: p.id,
      Name: p.name || "",
      City: p.city || "",
      Pincode: p.pincode || "",
      Mobile: p.mobile_number || "",
      Address: p.address || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Phlebotomists");
    XLSX.writeFile(wb, "Phlebotomists.xlsx");
  };

  // Reusable modal body (Add / Edit share the same fields)
  const renderFields = (form, setForm) => (
    <div className={styles.hmBody}>
      <div className={styles.hmGroup}>
        <label className={styles.hmLabel}>
          Name <span className={styles.hmReq}>*</span>
        </label>
        <input
          type="text"
          className={styles.hmInput}
          placeholder="Enter name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>

      <div className={styles.hmGroup}>
        <label className={styles.hmLabel}>Mobile Number</label>
        <input
          type="text"
          className={styles.hmInput}
          placeholder="e.g. +919876543210"
          value={form.mobile_number}
          onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
        />
      </div>

      <div className={styles.hmGroup}>
        <label className={styles.hmLabel}>Address</label>
        <input
          type="text"
          className={styles.hmInput}
          placeholder="Enter address"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>

      <div className={styles.hmRow}>
        <div className={styles.hmGroup}>
          <label className={styles.hmLabel}>City</label>
          <select
            className={styles.hmInput}
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          >
            <option value="">Select City</option>
            {form.city && !cities.some((c) => c.name === form.city) && (
              <option value={form.city}>{form.city}</option>
            )}
            {cities.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.hmGroup}>
          <label className={styles.hmLabel}>Pincode</label>
          <input
            type="text"
            className={styles.hmInput}
            placeholder="e.g. 560001"
            maxLength="10"
            value={form.pincode}
            onChange={(e) => setForm({ ...form, pincode: e.target.value })}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.doctorsManagement}>
      <div className={styles.sectionTitle}>
        <h2>Phlebotomists</h2>
      </div>

      {/* Toolbar: search (left) · actions (right) */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <input
            type="text"
            className={styles.toolbarSearch}
            placeholder="Search by name / city / pincode / mobile"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className={styles.toolbarRight}>
          <button className={styles.addHospitalBtn} onClick={openAdd}>
            + Add Phlebotomist
          </button>
          <button
            className={styles.exportIconBtn}
            title="Export to Excel"
            aria-label="Export to Excel"
            onClick={handleExport}
          >
            <FaFileExcel size={20} />
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>City</th>
              <th>Pincode</th>
              <th>Mobile</th>
              <th>Address</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{p.name}</td>
                <td>{p.city || "-"}</td>
                <td>{p.pincode || "-"}</td>
                <td>{p.mobile_number || "-"}</td>
                <td>{p.address || "-"}</td>
                <td style={{ textAlign: "right" }}>
                  <button
                    className={styles.editBtn}
                    onClick={() => openEdit(p)}
                    title="Edit"
                    style={{ marginRight: 6 }}
                  >
                    ✏️
                  </button>
                  <button
                    className={styles.editBtn}
                    onClick={() => handleDelete(p)}
                    title="Delete"
                    style={{ color: "crimson" }}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 16 }}>
                  No phlebotomists found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className={styles.hmBackdrop} onClick={() => setShowAdd(false)}>
          <div className={styles.hmCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.hmHeader}>
              <h5 className={styles.hmTitle}>Add Phlebotomist</h5>
              <button type="button" className={styles.hmClose} onClick={() => setShowAdd(false)}>
                &times;
              </button>
            </div>
            {renderFields(addForm, setAddForm)}
            <div className={styles.hmFooter}>
              <button className={styles.hmBtnSecondary} onClick={() => setShowAdd(false)}>
                Cancel
              </button>
              <button className={styles.hmBtnPrimary} onClick={handleAdd} disabled={loading}>
                {loading ? "Adding..." : "Add Phlebotomist"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {showEdit && (
        <div className={styles.hmBackdrop} onClick={closeEdit}>
          <div className={styles.hmCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.hmHeader}>
              <h5 className={styles.hmTitle}>Edit Phlebotomist</h5>
              <button type="button" className={styles.hmClose} onClick={closeEdit}>
                &times;
              </button>
            </div>
            {renderFields(editForm, setEditForm)}
            <div className={styles.hmFooter}>
              <button className={styles.hmBtnSecondary} onClick={closeEdit}>
                Cancel
              </button>
              <button className={styles.hmBtnPrimary} onClick={handleEdit} disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Phlebotomists;
